import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import DataTable from './subcomponents/DataTable';
import Indicator from './subcomponents/Indicator';

const useOverviewData = (dateRange) => {
  const [overviewData, setOverviewData] = useState({
    totalRegistrations: 0,
    totalOutlets: 0,
    courses: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadOverviewData = async () => {
      setLoading(true);
      try {
        const headers = { headers: { 'X-WP-Nonce': wpApiSettings.nonce } };
        const { dateMin, dateMax } = dateRange;

        const [totalRegistrationsRes, totalOutletsRes, courseDataRes] = await Promise.all([
          axios.post('/wp-json/draught/v1/get_total_registrations', { date_min: dateMin, date_max: dateMax }, headers),
          axios.post('/wp-json/draught/v1/get_total_outlets', { date_min: dateMin, date_max: dateMax }, headers),
          axios.post('/wp-json/draught/v1/get_course_data', { date_min: dateMin, date_max: dateMax }, headers),
        ]);

        setOverviewData({
          totalRegistrations: totalRegistrationsRes.data.data,
          totalOutlets: totalOutletsRes.data.data,
          courses: courseDataRes.data.data,
        });
      } catch (error) {
        console.error('Error loading overview data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadOverviewData();
  }, [dateRange]);

  return { overviewData, loading };
};

const useDetailData = (filters, view) => {
  const [detailData, setDetailData] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadDetailData = async () => {
    setLoading(true);
    try {
      const {
        selectedCourseId,
        selectedOutlet,
        selectedPostcode,
        dateMin,
        dateMax,
        displayEmployees,
        selectedView,
      } = filters;
      const requestUrl =
        selectedView === 'outlet'
          ? '/wp-json/draught/v1/get_outlet_data'
          : '/wp-json/draught/v1/get_user_data';

      const { data } = await axios.post(
        requestUrl,
        {
          course_id: selectedCourseId,
          outlet_name: selectedOutlet,
          postcode: selectedPostcode,
          date_min: dateMin,
          date_max: dateMax,
          is_employee: displayEmployees,
        },
        {
          headers: { 'X-WP-Nonce': wpApiSettings.nonce },
        }
      );

      setDetailData(Object.values(data.data));
    } catch (error) {
      console.error('Error loading detail data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Call loadDetailData whenever filters change and view is 'detail-course'
  useEffect(() => {
    if (view === 'detail-course') {
      loadDetailData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, view]);

  return { detailData, loading };
};

const Dashboard = () => {
  const [view, setView] = useState('dashboard');
  const [filters, setFilters] = useState({
    selectedCourseId: null,
    selectedOutlet: '',
    selectedPostcode: '',
    dateMin: new Date(`${new Date().getFullYear()}-01-01`),
    dateMax: null,
    displayEmployees: false,
    selectedView: 'outlet',
  });
  const [overviewDateRange, setOverviewDateRange] = useState({
    dateMin: new Date(`${new Date().getFullYear()}-01-01`),
    dateMax: null,
  });

  const { overviewData, loading: overviewLoading } = useOverviewData(overviewDateRange);
  const { detailData, loading: detailLoading } = useDetailData(filters, view);

  const detailHeaders = useMemo(() => {
    if (view === 'dashboard') {
      return {
        Name: { name: 'Name', type: 'string', clickable: true },
        NumberOfUsers: { name: 'Number of Users', type: 'number' },
        UsersVerified: { name: 'Number of Verified Users', type: 'string' },
        NumberOfUsersStarted: { name: 'Courses Started', type: 'number' },
        NumberOfUsersCompleted: { name: 'Courses Completed', type: 'number' },
        TotalSales: { name: 'Total Sales', type: 'money' },
        TotalDiscount: { name: 'Total Discount', type: 'money' },
        NumItemsSold: { name: 'Items Sold', type: 'number' },
      };
    } else if (filters.selectedView === 'outlet') {
      return {
        "Outlet name": { name: 'Outlet Name', type: 'string' },
        Postcode: { name: 'Postcode', type: 'string' },
        "Number of courses": { name: 'Number of Courses', type: 'number' },
        "Number of completed courses": { name: 'Number of Completed Courses', type: 'number' },
        "Number of users": { name: 'Number of Users', type: 'number' },
        "Total sales": { name: 'Total Sales', type: 'money' },
        "Discount amount": { name: 'Discount Amount', type: 'money' },
      };
    } else {
      return {
        FirstName: { name: 'First Name', type: 'string' },
        LastName: { name: 'Last Name', type: 'string' },
        OutletName: { name: 'Outlet', type: 'string' },
        Email: { name: 'Email', type: 'string' },
        EmailConfirmed: { name: 'Email Confirmed', type: 'string' },
        Job: { name: 'Job', type: 'string' },
        Postcode: { name: 'Postcode', type: 'string' },
        LastLogin: { name: 'Last Login', type: 'datetime' },
        RegistrationDate: { name: 'Registration Date', type: 'date' },
        NumberOfCourses: { name: 'Number of Courses', type: 'number' },
        NumberOfCompletedCourses: { name: 'Number of Completed Courses', type: 'number' },
      };
    }
  }, [view, filters.selectedView]);

  const handleFilterChange = (key, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
  };

  const viewOptions = [
    { text: 'Outlet', value: 'outlet' },
    { text: 'Individual', value: 'individual' },
  ];

  const isLoading = overviewLoading || detailLoading;

  return (
    <div>
      {isLoading && (
        <div className="draught-dashboard__spinner">
          <div className="draught-dashboard__spinner-inner">
            {[...Array(9)].map((_, index) => (
              <div
                key={index}
                className={`draught-dashboard__spinner-inneritem draught-dashboard__spinner-inner-${index + 1
                  }`}
              />
            ))}
          </div>
        </div>
      )}

      {view === 'dashboard' ? (
        <div className="draught-dashboard">
          <div className="draught-dashboard__indicators">
            <Indicator title="Total Registrations" value={overviewData.totalRegistrations} />
            <Indicator title="Total Outlets" value={overviewData.totalOutlets} />
          </div>
          <div className="draught-dashboard__recap">
            <h2>Data Overview</h2>
            <form className="ui form">
              <div className="field">
                <div className="fields">
                  <div className="field">
                    <label>From</label>
                    <DatePicker
                      selected={overviewDateRange.dateMin}
                      onChange={(date) => {
                        setOverviewDateRange((prevRange) => ({ ...prevRange, dateMin: date }))
                        setFilters((prevFilters) => ({
                          ...prevFilters,
                          dateMin: date,
                        }));
                      }
                      }
                    />
                  </div>
                  <div className="field">
                    <label>To</label>
                    <DatePicker
                      selected={overviewDateRange.dateMax}
                      onChange={(date) => {
                        setOverviewDateRange((prevRange) => ({ ...prevRange, dateMax: date }))
                        setFilters((prevFilters) => ({
                          ...prevFilters,
                          dateMax: date,
                        }));
                      }
                      }
                    />
                  </div>

                </div>
              </div>
            </form>
            <DataTable
              data={overviewData.courses}
              headers={detailHeaders}
              onSelected={(course) => {
                handleFilterChange('selectedCourseId', course.Id);
                setView('detail-course');
              }}
            />
          </div>
        </div>
      ) : (
        <div className="draught-dashboard__detail-course">
          <div className="field">
            <div className="sui-down field">
              <button role="button" className="ui button"
                onClick={() => setView('dashboard')}>
                <i className="arrow left icon"></i>
                Back</button>
            </div>
          </div>
          <h2>
            Data for course:{' '}
            {filters.selectedCourseId
              ? overviewData.courses.find((opt) => opt.Id === +filters.selectedCourseId)?.Name
              : 'All courses'}
          </h2>
          <form className="ui form">
            <div className="fields">
              <div className="field">
                <label>Courses</label>
                <select
                  value={filters.selectedCourseId || ''}
                  onChange={(e) => handleFilterChange('selectedCourseId', e.target.value)}
                >
                  <option value="">All courses</option>
                  {overviewData.courses.map((option) => (
                    <option key={option.Id} value={option.Id}>
                      {option.Name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Outlets</label>
                <input
                  type="text"
                  value={filters.selectedOutlet}
                  onChange={(e) => handleFilterChange('selectedOutlet', e.target.value)}
                  placeholder="Search outlets..."
                />
              </div>
              <div className="field">
                <label>Postcode</label>
                <input
                  type="text"
                  value={filters.selectedPostcode}
                  onChange={(e) => handleFilterChange('selectedPostcode', e.target.value)}
                  placeholder="Search by postcode..."
                />
              </div>
            </div>
            <div className="fields">
              <div className="field">
                <label>Views</label>
                <select
                  value={filters.selectedView}
                  onChange={(e) => handleFilterChange('selectedView', e.target.value)}
                >
                  {viewOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.text}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>From</label>
                <DatePicker
                  selected={filters.dateMin}
                  onChange={(date) => handleFilterChange('dateMin', date)}
                />
              </div>
              <div className="field">
                <label>To</label>
                <DatePicker
                  selected={filters.dateMax}
                  onChange={(date) => handleFilterChange('dateMax', date)}
                />
              </div>

              <div className="ui toggle checkbox">
                <input
                  type="checkbox"
                  checked={filters.displayEmployees}
                  onChange={() => handleFilterChange('displayEmployees', !filters.displayEmployees)}
                />
                <label>See C&amp;C Employee Data</label>
              </div>
            </div>
          </form>

          <h2>{detailData.length} results</h2>
          <DataTable data={detailData} headers={detailHeaders} />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
